import {
    Box,
    Text,
    Tooltip,
    useDisclosure,
    type BoxProps,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

export type Environment =
    | 'production'
    | 'beta'
    | 'alpha'
    | 'dev'
    | 'local'
    | 'staging'
    | 'test';

export interface EnvironmentTagProps extends BoxProps {
    /** Custom text to render in the environment tag */
    renderText?: (env: Environment) => React.ReactNode;

    /** Used for visually testing environment */
    testEnv?: Environment;
}

/**
 * Environment indicator component that displays the current runtime environment.
 * Shows a labeled tag for non-production environments (beta, dev, local, staging)
 * to help users identify which environment they're working in.
 */
export const EnvironmentTag = ({
    testEnv,
    ...boxProps
}: EnvironmentTagProps) => {
    const { isOpen, onClose } = useDisclosure({
        defaultIsOpen: true,
    });

    const [env, setEnv] = useState<Environment | null>(null);

    useEffect(() => {
        setEnv(testEnv ?? getEnvironment());
    }, [testEnv]);

    // no need to display this tag in production, no environment set, or if the tag is closed
    if (env === null || env === 'production' || !isOpen) {
        return null;
    }

    return (
        <Tooltip
            label={
                <>
                    <span>
                        Currently running in <b>{env.toUpperCase()}</b>{' '}
                        environment
                    </span>
                    <br />
                    <span>Click to close this tag</span>
                </>
            }
        >
            <Box
                zIndex="modal"
                position="fixed"
                w="50px"
                h="50px"
                top="0"
                left="0"
                overflow="hidden"
                userSelect="none"
                {...boxProps}
                sx={{
                    pointerEvents: 'none',
                    ...boxProps.sx,
                    '@media print': { display: 'none' },
                }}
            >
                <Text
                    as="span"
                    position="absolute"
                    top="6px"
                    left="-32px"
                    w="100px"
                    py="6px"
                    fontSize="9px"
                    fontWeight="700"
                    lineHeight="1"
                    transform="rotate(-45deg)"
                    textTransform="uppercase"
                    fontFamily="monospace"
                    textAlign="center"
                    textShadow="xs"
                    color="white"
                    bg={getColor(env)}
                    onClick={onClose}
                >
                    {env}
                </Text>
            </Box>
        </Tooltip>
    );
};

// Detect the current environment based on the window location
export const getEnvironment = (): Environment => {
    if (typeof window === 'undefined') {
        return 'production';
    }

    const hostname = window.location.hostname;
    const isLocal =
        hostname.includes('local') ||
        hostname === '0.0.0.0' ||
        hostname === '127.0.0.1';

    return (
        isLocal
            ? 'local'
            : hostname.includes('beta')
            ? 'beta'
            : hostname.includes('alpha')
            ? 'alpha'
            : hostname.includes('dev')
            ? 'dev'
            : hostname.includes('staging')
            ? 'staging'
            : 'production'
    ) as Environment;
};

const getColor = (env: Environment) => {
    switch (env) {
        case 'beta':
            return 'orange.600';
        case 'local':
        case 'dev':
            return 'green.600';
        // the rest
        default:
            return 'gray.600';
    }
};
